import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import {
  createMeterReader,
  deleteManufacturere,
  getActiveMeterReadingStatuses,
  getMeterReaderAssignedRoutes,
  getMeterReaderDetail,
  getMeterReaderList,
  getMeterReaderReadings,
  deleteMeterReadingFormat,
  getMeterReadingFormat,
  getMeterReadingFormatDetail,
  getMeterReadingStatusList,
  getMeterReasonCodesList,
  getReadFormatFields,
  getUserUtilityServices,
  patchMeterReadingFormat,
  postMeterReadingFormat,
  postMeterReadingStatus,
  postMeterReasonCodes,
  updateMeterReader,
  updateMeterReaderAccess,
  updateMeterReadingStatus,
  updateMeterReasonCodes,
  updateReasonCodeStatus,
  verifyMeterReaderEmail,
  getCycleMonthList,
  getCycleNameList,
  getRouteNameList,
  createResourceType,
  createResourceSubType,
  editResourceTypeSubtype,
  getReadMethods,
  addWarehouse,
  updateWarehouse,
  getWarehouses,
  getMeterManufacturers,
  getMeterModelDetail,
  getMeterModels,
  getReadingStatus,
} from "./api";
import { queryClient } from "@shared/api/queries/queryClient";
import { useToast } from "@shared/hooks/use-toast";
import { toast } from "@shared/ui/use-toast";
export const useMeterReadingFormat = (
  remoteUtilityId: string,
  search: string,
) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.settings.readingFormatList(search),
    () => getMeterReadingFormat(remoteUtilityId, search),
    {
      staleTime: 0,
      enabled: !!remoteUtilityId,
      refetchOnMount: true,
    },
  );

export const useUserUtilityServices = (utilityId: any) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.settings.userUtilityServices(),
    () => getUserUtilityServices(utilityId),
    {
      staleTime: 30 * 60 * 1000,
      enabled: !!utilityId,
    },
  );

export const useDeleteManufacture = () => {
  const { toast } = useToast();
  return useSmartMutation((id: number) => deleteManufacturere(id), {
    onSuccess: (_data, id) => {
      toast({
        title: "Success",
        description: "Manufacturer deleted successfully!",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["manufacturer", "meterManufacturers"],
      });
    },
  });
};

export const usePostMeterReadingFormat = () =>
  useSmartMutation(postMeterReadingFormat, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Format created successfully.",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["mx", "format-list"],
      });
    },
  });

export const useAddWarehouse = () =>
  useSmartMutation(addWarehouse, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Warehouse registered successfully.",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["mx", "warehouse-list"],
      });
    },
  });

export const useUpdateWarehouse = () =>
  useSmartMutation(
    ({ id, payload }: { id: string; payload: any }) => updateWarehouse(id, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Warehouse updated successfully.",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "warehouse-list"],
        });
      },
    }
  );

export const useMeterReadingFormatDetail = (id: string, remoteUtilityId) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.settings.viewMeterReadingFormatDetail(id),
    () => getMeterReadingFormatDetail(id, remoteUtilityId),
    { staleTime: 0, refetchOnMount: true, enabled: !!remoteUtilityId && !!id },
  );
};

export const usePatchMeterReadingFormat = () =>
  useSmartMutation(
    ({ id, payload }: { id: number; payload: any }) =>
      patchMeterReadingFormat(id, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Format updated successfully.",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "format-list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meter-reading-format-detail"],
        });
      },
    },
  );

export const useDeleteMeterReadingFormat = () =>
  useSmartMutation(
    (id: number) => deleteMeterReadingFormat(id),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Format deleted successfully.",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "format-list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meter-reading-format-detail"],
        });
      },
    },
  );


export const useMeterReaderList = ({
  page,
  remoteUtilityId,
  search,
}: {
  page: number;
  remoteUtilityId: string;
  search?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.settings.viewMeterReaders(page, search),
    () => getMeterReaderList({ page, remoteUtilityId, search }),
    { enabled: !!remoteUtilityId },
  );
};

export const useUpdateMeterReader = () =>
  useSmartMutation(
    ({ id, payload }: { id: number; payload: any }) =>
      updateMeterReader(id, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Meter Reader updated successfully",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meter-reader-detail"],
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meter-reader"],
        });
      },
    },
  );

export const useMeterReadingStatusList = (
  remoteUtilityId: string,
  page: number,
  limit: any,
  search?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.settings.meterReadingStatusList(page, search),
    () => getMeterReadingStatusList(remoteUtilityId, page, limit, search),
    { enabled: !!remoteUtilityId },
  );
};

export const useUpdateMeterReadingStatus = () =>
  useSmartMutation(
    ({ id, payload }: { id: number; payload: any }) =>
      updateMeterReadingStatus(String(id), payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["mx", "meter-reading-status"],
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "active-reading-status"],
        });
      },
    },
  );

export const useUpdateReasonCodeStatus = () =>
  useSmartMutation(
    ({ id, payload }: { id: number; payload: any }) =>
      updateReasonCodeStatus(String(id), payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["mx", "reason-code"],
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "reason-code-list"],
        });
      },
    },
  );
export const usePostMeterReadingStatus = () =>
  useSmartMutation(postMeterReadingStatus, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Meter reading status added successfully",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["mx", "meter-reading-status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["mx", "active-reading-status"],
      });
    },
  });

export const useMeterReasonCodesList = (
  remoteUtilityId: string,
  page: number,
  limit: number,
  search?: string,
  meterReadingStatus?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.settings.meterReasonCodesList(
      page,
      search,
      meterReadingStatus,
    ),
    () =>
      getMeterReasonCodesList(
        remoteUtilityId,
        page,
        limit,
        search,
        meterReadingStatus,
      ),
  );
};

export const useUpdateMeterReasonCodes = () =>
  useSmartMutation(
    ({ id, payload }: { id: number; payload: any }) =>
      updateMeterReasonCodes(String(id), payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Activation status updated successfully",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "reason-code-list"],
        });
      },
    },
  );

export const usePostMeterReasonCodes = () =>
  useSmartMutation(postMeterReasonCodes, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["mx", "reason-code-list"],
      });
    },
  });

export const useActiveMeterReadingStatuses = (remoteUtilityId: string) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.settings.viewActiveMeterStatuses(),
    () => getActiveMeterReadingStatuses(remoteUtilityId),
    { enabled: !!remoteUtilityId },
  );

export const useCreateMeterReader = () =>
  useSmartMutation(createMeterReader, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Meter reader created successfully",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["mx", "meter-reader"],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

export const useGetMeterReaderDetail = (remoteUtilityId: any, id: any) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.settings.meterReaderDetail(id),
    () => getMeterReaderDetail(id, remoteUtilityId),
    { enabled: !!remoteUtilityId && !!id },
  );

export const useGetMeterReaderAssignedRoutes = (
  remoteUtilityId: any,
  id: any,
  filters?: { cycle?: string; cycle_month?: string; route?: string },
) =>
  useSmartQuery(
    [
      ...QueryKeyFactory.module.mx.settings.meterReaderAssignedRoutes(id),
      filters,
    ],
    () => getMeterReaderAssignedRoutes(id, remoteUtilityId, filters),
    { enabled: !!remoteUtilityId && !!id },
  );

export const useGetMeterReaderReadings = (remoteUtilityId: any, id: any) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.settings.meterReaderReadings(id),
    () => getMeterReaderReadings(id, remoteUtilityId),
    { enabled: !!remoteUtilityId && !!id },
  );

export const useVerifyMeterReaderEmail = () =>
  useSmartMutation(verifyMeterReaderEmail, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Meter reader email verified successfully",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["mx", "meter-reader"],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

export const useUpdateMeterReaderAccess = () =>
  useSmartMutation<any, Error, { id: string; payload: any }>(
    ({ id, payload }) => updateMeterReaderAccess(id, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Meter reader access updated successfully",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meter-reader-detail"],
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meter-reader"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    },
  );

export const useGetReadFormatFields = (remoteUtilityId: any) =>
  useSmartQuery(
    QueryKeyFactory.module.mx.settings.readFormatFields(remoteUtilityId),
    () => getReadFormatFields(remoteUtilityId),
    { enabled: !!remoteUtilityId },
  );

export const useGetCycleMonthList = (meterReaderId: string) => {
  return useSmartQuery(
    ["mx", "settings", "cycleMonthList", meterReaderId],
    () => getCycleMonthList(meterReaderId),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: !!meterReaderId,
    },
  );
};

export const useGetCycleNameList = (meterReaderId: string) => {
  return useSmartQuery(
    ["mx", "settings", "cycleNameList", meterReaderId],
    () => getCycleNameList(meterReaderId),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: !!meterReaderId,
    },
  );
};

export const useGetRouteNameList = (meterReaderId: string) => {
  return useSmartQuery(
    ["mx", "settings", "routeNameList", meterReaderId],
    () => getRouteNameList(meterReaderId),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: !!meterReaderId,
    },
  );
};

export const useCreateResourceType = () => {
  return useSmartMutation(createResourceType, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Resource type created successfully",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["mx", "shared", "resource-type-list"],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useCreateResourceSubType = () => {
  return useSmartMutation(createResourceSubType, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Resource sub type created successfully",
        variant: "default",
      });
      queryClient.refetchQueries({
        queryKey: ["mx", "shared", "resource-type-list"],
      });
      queryClient.refetchQueries({
        queryKey: ["mx", "shared", "resource-subtype-list"],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useEditResourceTypeSubtype = () => {
  return useSmartMutation(editResourceTypeSubtype, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Resource type updated successfully",
        variant: "default",
      });
      queryClient.refetchQueries({
        queryKey: ["mx", "shared", "resource-type-list"],
      });
      queryClient.refetchQueries({
        queryKey: ["mx", "shared", "resource-subtype-list"],
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useGetReadMethods = () => {
  return useSmartQuery(
    ["mx", "settings", "read-methods"],
    () => getReadMethods(),
    {
      staleTime: 30 * 60 * 1000,
    },
  );
};

export const useWarehouses = (
  remoteUtilityId: string,
  search: string,
  page: number = 1
) =>
  useSmartQuery(
    ["mx", "warehouse-list", search, page],
    () => getWarehouses(remoteUtilityId, search, page),
    {
      staleTime: 0,
      enabled: !!remoteUtilityId,
      refetchOnMount: true,
    }
  );

export const useMeterManufacturers = (params: {
  remote_utility_id: string;
  is_pagination_required?: boolean;
  page?: number;
  limit?: number;
  search_data?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.manufacturer.meterManufacturers(params),
    () => getMeterManufacturers(params)
  );
};

export const useMeterModelDetail = (params: {
  remote_utility_id: any;
  meter_model_id: string;
}) => {
  const isEnabled = !!params.remote_utility_id && !!params.meter_model_id;

  return useSmartQuery(
    QueryKeyFactory.module.mx.manufacturer.meterModelDetail(params),
    () => getMeterModelDetail(params),
    {
      enabled: isEnabled,
    }
  );
};

export const useMeterModels = (params: {
  remote_utility_id: any;
  meter_type: any;
  searchData: any;
  manufacturer: any;
  is_active: any;
  page: any;
  limit: any;
}) => {
  const isEnabled = !!params.remote_utility_id;

  return useSmartQuery(
    QueryKeyFactory.module.mx.manufacturer.meterModels(params),
    () => getMeterModels(params),
    {
      enabled: isEnabled,
    }
  );
};

export const useReadingStatus = () => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.readingStatus(),
    () => getReadingStatus(),
  );
};
